/* Combobox: a form control (the same box as Input) with a listbox beneath it.
 * The list is positioned over whatever follows the field and sits at the
 * dropdown layer so that it clears a dialog's edge. */

.root {
  position: relative;
}

.control {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  height: 40px;
  padding: 0 var(--space-3);
  background: var(--color-surface);
  border: var(--border-width-thin) solid var(--color-border-default);
  border-radius: var(--radius-md);
  transition:
    border-color var(--duration-fast) var(--easing-standard),
    box-shadow var(--duration-fast) var(--easing-standard);
}

.control:hover {
  border-color: var(--color-border-strong);
}

.control:focus-within {
  border-color: var(--color-border-focus);
  box-shadow: 0 0 0 2px var(--color-focus-halo);
}

.control:has(input:disabled) {
  background: var(--color-surface-sunken);
  opacity: var(--opacity-disabled);
}

.controlInvalid {
  border-color: var(--color-danger-emphasis);
}

.input {
  flex: 1;
  min-width: 0;
  height: 100%;
  background: none;
  border: 0;
  font-size: var(--text-base);
  color: var(--color-text-primary);
}

.input:focus {
  outline: none;
}

.input::placeholder {
  color: var(--color-text-tertiary);
}

.adornment {
  display: inline-flex;
  flex: none;
  align-items: center;
  color: var(--color-text-tertiary);
}

.adornmentButton {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: var(--radius-sm);
  color: var(--color-text-secondary);
  cursor: pointer;
}

.adornmentButton:hover {
  background: var(--color-surface-hover);
  color: var(--color-text-primary);
}

.adornmentButton:focus-visible {
  outline: 2px solid var(--color-border-focus);
  outline-offset: 1px;
}

.list {
  position: absolute;
  inset-inline: 0;
  top: calc(100% + var(--space-1));
  z-index: var(--z-dropdown);
  max-height: 280px;
  padding: var(--space-2);
  overflow-y: auto;
  overscroll-behavior: contain;
  background: var(--color-surface-raised);
  border: var(--border-width-thin) solid var(--color-border-default);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-high);
}

.listHidden {
  display: none;
}

.item {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  width: 100%;
  min-height: var(--row-height);
  padding: var(--space-1) var(--space-3);
  text-align: start;
  border-radius: var(--radius-sm);
  font-size: var(--text-base);
  color: var(--color-text-primary);
  cursor: pointer;
  user-select: none;
}

/* Fill and an accent rule: colour alone would not carry the state. */
.itemActive {
  background: var(--color-surface-selected);
  box-shadow: inset 2px 0 0 var(--color-accent-emphasis);
}

.item[aria-selected="true"] .itemLabel {
  font-weight: var(--font-weight-medium);
}

/* A long branch name wraps rather than truncating: the name is the thing
 * being chosen, and a truncated one cannot be told from its sibling. */
.itemLabel {
  flex: 1 1 auto;
  min-width: 0;
  overflow-wrap: anywhere;
  font-family: var(--font-family-mono);
  font-size: var(--text-sm);
}

.itemTrailing {
  display: inline-flex;
  flex: none;
  gap: var(--space-1);
  align-items: center;
}

.status,
.footer {
  padding: var(--space-3);
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
}

.footer {
  border-top: var(--border-width-thin) solid var(--color-border-default);
  margin-top: var(--space-1);
}
